import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Tooltip,
  Typography,
} from '@mui/material'
import { AddShoppingCart, InfoOutlined, List, RecordVoiceOver } from '@mui/icons-material'
import moment from 'moment'
import * as React from 'react'
import CardWithTitle from '../common/CardWithTitle/CardWithTitle'
import { IQuoteFormValues } from './QuoteFormValues'
import { GetEOCPatientReferralInfo } from 'src/services/LookupService'
import { EOCPatientReferralSummaryResult } from 'src/viewModels/EOCPatientReferralSummaryResult'
import { IQuoteDTO } from 'src/viewModels/quotes/QuoteDTO'
import FullWidthField from 'src/common/FullWidthField'
import QuoteTypeNameConstants from 'src/constants/QuoteTypeNameConstants'
import { QuoteInboxItem } from 'src/viewModels/workflow/QuoteInboxItem'
import { QuoteStatus } from 'src/utils/QuoteStatus'
import { InboxActionSummary } from 'src/viewModels/workflow/InboxActionSummary'
import ReferralContactModal from 'src/patients/EpisodesOfCare/ReferralContactModal'
import BundleClaimsModal from './QuoteBills/BundleClaimsModal'
import { Link } from 'react-router-dom'
import { QuoteType } from 'src/utils/QuoteType'

const QuoteStatusNames: { [key: string]: any } = {
  0: 'Needs Quote',
  1: 'Pending',
  2: 'Needs Recon',
  3: 'Reconciled',
  4: 'Complete',
  5: 'Imported',
  6: 'Draft',
  7: 'Quoted',
  8: 'Pending Contract',
  9: 'Pending Care Coordinator',
  10: 'Pending Finance',
  12: 'Dismissed',
}

interface IQuoteDetailsProps {
  disableInvoiceButtons: boolean
  values: IQuoteFormValues
  takeoverOfCareDate?: Date
  selectedQuote?: IQuoteDTO
  assistantWarning?: boolean
  viewMode?: boolean
  updateMain?: () => void
  isAdjusterEmailModalOpen?: boolean
  rerouteToPath?: (path: string) => void
  createInvoice?: (quoteInboxItem: QuoteInboxItem, actionId: number) => void
  quoteInboxItem: QuoteInboxItem
  isReferralContactModalOpen?: boolean
  openReferralContactModal?: () => void
  openBundleClaimsModal?: () => void
  excludeAllLineItems?: (forceUpdateHandler: () => void) => void
  deleteAllLineItems?: (reconcile: boolean, forceUpdateHandler: () => void) => void
}

class QuoteDetails extends React.Component<IQuoteDetailsProps> {
  constructor(props: IQuoteDetailsProps) {
    super(props)
  }

  public async componentDidMount() {
    this.getEOCPatientReferralInfoByEOCNumber()
  }

  public getEOCPatientReferralInfoByEOCNumber() {
    const { episodeOfCareNumber } = this.props.values
    GetEOCPatientReferralInfo(episodeOfCareNumber).then(
      (response: EOCPatientReferralSummaryResult) => {
        this.setState({
          eocId: response.eocId,
          patientId: response.patientId,
          referralId: response.referralId,
        })
      }
    )
  }

  public getLinkPath() {
    return (
      '/patients/patient/' +
      this.state.patientId +
      '/referral/' +
      this.state.referralId +
      '/episodeOfCare/' +
      this.state.eocId
    )
  }

  private renderEocStatus() {
    const eocStatus = this.props.selectedQuote?.episodeOfCareStatus?.trim()
    const coordinationStatus =
      this.props.selectedQuote?.episodeOfCareCoordinationStatus?.trim()
    if (!eocStatus && !coordinationStatus) {
      return null
    }

    return (
      <Typography
        variant="caption"
        color="primary"
        component="div"
        sx={{ lineHeight: 1.2, fontSize: '0.8rem', mt: 0.25 }}
      >
        <strong>{eocStatus}</strong>
        {coordinationStatus && (
          <Box component="span" sx={{ fontWeight: 600, ml: 0.5 }}>
            ({coordinationStatus})
          </Box>
        )}
      </Typography>
    )
  }

  public state = {
    eocId: '',
    patientId: '',
    referralId: '',
    pdfCreated: false,
    pdfLoading: false,
    invoiceModalIsOpen: false,
    isLoading: false,
  }

  public render() {
    const { values, disableInvoiceButtons } = this.props
    const hasUnbundledClaims = (this.props.selectedQuote?.eocUnbundledClaims ?? 0) > 0
    let invoiceAction = undefined as unknown as InboxActionSummary
    let combined1500Action = undefined as unknown as InboxActionSummary
    let consumeQuoteAction = undefined as unknown as InboxActionSummary
    let copyQuoteAction = undefined as unknown as InboxActionSummary

    if (
      this.props.quoteInboxItem?.actions.filter(
        (x) => x.uiGroupButtonText != undefined
      ) ||
      (this.props.quoteInboxItem?.actions.filter((x) => x.staticActionId != undefined) &&
        this.props.quoteInboxItem?.actions.filter((x) => x.uiGroupButtonText != undefined)
          .length > 0) ||
      this.props.quoteInboxItem?.actions.filter((x) => x.staticActionId == 19).length > 0
    ) {
      invoiceAction = this.props.quoteInboxItem?.actions.filter(
        (x) => x.uiGroupButtonText != undefined && x.staticActionId == 17
      )[0]
      combined1500Action = this.props.quoteInboxItem?.actions.filter(
        (x) => x.staticActionId == 19
      )[0]
      consumeQuoteAction = this.props.quoteInboxItem?.actions.filter(
        (x) => x.staticActionId == 22
      )[0]
      copyQuoteAction = this.props.quoteInboxItem?.actions.filter(
        (x) => x.staticActionId == 23
      )[0]
    }
    const headerButtons = (
      <Grid container spacing={1} alignItems="center">
        {this.props.selectedQuote?.globalInvoiceId && (
          <Grid item>
            <Button
              size="small"
              color="primary"
              variant="contained"
              onClick={() =>
                window.open(
                  `/workflow/invoice/${this.props.selectedQuote?.globalInvoiceId}/default-inbox`,
                  '_blank'
                )
              }
            >
              Open Invoice
            </Button>
          </Grid>
        )}
        {this.props.selectedQuote?.status == QuoteStatus.Reconciled && invoiceAction && (
          <Grid item>
            <Button
              size="small"
              color="primary"
              variant="contained"
              disabled={disableInvoiceButtons}
              onClick={async () => {
                this.setState({ isLoading: true })

                await this.props.createInvoice!(
                  this.props.quoteInboxItem!,
                  invoiceAction.id!
                )
              }}
            >
              {this.state.isLoading ? (
                <div
                  style={{
                    position: 'fixed',
                    display: 'flex',
                    width: '100%',
                    height: '100%',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    cursor: 'pointer',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: '2',
                  }}
                >
                  <CircularProgress size={100} />
                </div>
              ) : (
                invoiceAction.actionName
              )}
            </Button>
          </Grid>
        )}
        {combined1500Action && (
          <Grid item>
            <Link
              to={`/workflow/quote/${this.props.selectedQuote?.id}/createcombined1500?inboxItemId=${this.props.quoteInboxItem.id}&actionId=${combined1500Action.id}`}
              style={{
                textDecoration: 'none',
              }}
            >
              <Button
                size="small"
                color="primary"
                variant="contained"
                disabled={disableInvoiceButtons}
              >
                {combined1500Action.actionName}
              </Button>
            </Link>
          </Grid>
        )}
        {consumeQuoteAction && (
          <Grid item>
            <Link
              to={`/workflow/quote/consume/${this.props.selectedQuote?.id}?inboxItemId=${this.props.quoteInboxItem.id}&actionId=${consumeQuoteAction.id}&displayComment=${consumeQuoteAction.displayComment}&commentRequired=${consumeQuoteAction.requireComment}`}
              style={{
                textDecoration: 'none',
              }}
            >
              <Button
                size="small"
                color="primary"
                variant="contained"
                disabled={disableInvoiceButtons}
              >
                Consume Quote
              </Button>
            </Link>
          </Grid>
        )}
        {copyQuoteAction && (
          <Grid item>
            <Link
              to={`/workflow/quote/${this.props.selectedQuote?.id}/copy?inboxItemId=${this.props.quoteInboxItem.id}&actionId=${copyQuoteAction.id}`}
              style={{
                textDecoration: 'none',
              }}
            >
              <Button
                size="small"
                color="primary"
                variant="contained"
                disabled={disableInvoiceButtons}
              >
                {copyQuoteAction.actionName}
              </Button>
            </Link>
          </Grid>
        )}
      </Grid>
    )

    const statusText =
      this.props.selectedQuote?.status !== undefined
        ? QuoteStatusNames[this.props.selectedQuote.status]
        : ''

    return (
      <>
        {this.state.pdfLoading && (
          <div
            style={{
              position: 'fixed',
              display: 'flex',
              width: '100%',
              height: '100%',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              cursor: 'pointer',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 500,
            }}
          >
            <CircularProgress size={100} />
          </div>
        )}
        {this.props.isReferralContactModalOpen && (
          <ReferralContactModal selectedReferralId={this.state.referralId!} />
        )}
        {this.props.openBundleClaimsModal && <BundleClaimsModal />}
        <CardWithTitle
          title={
            this.props.selectedQuote?.type != undefined
              ? QuoteTypeNameConstants[this.props.selectedQuote?.type] + ' Quote'
              : ''
          }
          icon={List}
          headerStyle={{ marginBottom: 0 }}
          titleComponent={headerButtons}
          isTitleComponentPushedRight={true}
          subTitle={
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
              <Typography component="span" variant="subtitle2" sx={{ ml: 0.5 }}>
                {`(Status: ${statusText})`}
              </Typography>
              {this.props.selectedQuote?.isConsumer && (
                <Chip
                  label="CONSUMER QUOTE"
                  size="small"
                  variant="outlined"
                  icon={
                    <span
                      style={{
                        fontSize: '0.9rem',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      🦈
                    </span>
                  }
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: 'rgba(128, 128, 128, 0.2)',
                    color: (theme) =>
                      theme.palette.mode === 'dark'
                        ? theme.palette.common.white
                        : theme.palette.primary.main,
                    borderColor: (theme) =>
                      theme.palette.mode === 'dark'
                        ? theme.palette.grey[500]
                        : 'rgba(128, 128, 128, 0.2)',
                    height: 'auto',
                  }}
                />
              )}
            </Box>
          }
          subTitleInfo={this.props.selectedQuote?.latestStatusComment}
        >
          <Grid container item direction="row" spacing={2} rowSpacing={2}>
            <Grid item style={{ whiteSpace: 'nowrap' }}>
              <Typography variant="body2" component="span">
                <strong>Patient:</strong> {values.patientName}
                {values.dateOfBirth && (
                  <strong style={{ fontSize: '0.875rem' }}>
                    {' [' + moment(values.dateOfBirth).format('L') + ']'}
                  </strong>
                )}
              </Typography>
            </Grid>
            <Grid item style={{ whiteSpace: 'nowrap' }}>
              <Typography variant="body2" component="span">
                <strong>Start:</strong>{' '}
                {values.startDate ? moment(values.startDate).format('L') : 'N/A'}
                {(this.props.selectedQuote?.type == QuoteType.Injection ||
                  this.props.selectedQuote?.type == QuoteType.Surgical) && (
                  <Tooltip
                    title={`Represents Surgery Date when ${
                      QuoteTypeNameConstants[this.props.selectedQuote.type]
                    } bundle`}
                  >
                    <InfoOutlined
                      fontSize="small"
                      sx={{ ml: 0.5, verticalAlign: 'middle' }}
                    />
                  </Tooltip>
                )}
              </Typography>
            </Grid>
            <Grid item style={{ whiteSpace: 'nowrap' }}>
              <Typography variant="body2" component="span">
                <strong># Days:</strong>{' '}
                {this.props.selectedQuote?.bundleDurationDays ?? 'N/A'}
              </Typography>
            </Grid>
            <Grid item style={{ whiteSpace: 'nowrap' }}>
              <Typography variant="body2" component="span">
                <strong>End:</strong>{' '}
                {this.props.selectedQuote?.endDate
                  ? moment(this.props.selectedQuote?.endDate).format('L')
                  : 'N/A'}
              </Typography>
            </Grid>
            <Grid item style={{ whiteSpace: 'nowrap' }}>
              <Typography variant="body2" component="span">
                <strong>Body Part:</strong> {values.bodyPartName}
              </Typography>
            </Grid>
            <Grid item style={{ whiteSpace: 'nowrap' }}>
              <Typography variant="body2" component="span">
                <strong>EOC/Legacy:</strong>{' '}
                <Button
                  variant="text"
                  color="primary"
                  size="small"
                  sx={{ userSelect: 'text' }}
                  style={{
                    textTransform: 'none',
                    padding: 0,
                    minWidth: 0,
                    background: 'none',
                    border: 'none',
                    boxShadow: 'none',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    fontWeight: 'normal',
                    fontSize: 'inherit',
                    lineHeight: 'inherit',
                  }}
                  onClick={() => {
                    sessionStorage.setItem('patientTab', '0')
                    sessionStorage.setItem('eocTab', '1')
                    window.open(this.getLinkPath())
                  }}
                >
                  {values.episodeOfCareOrLeagacyId}
                </Button>
              </Typography>
              {this.renderEocStatus()}
            </Grid>
            <Grid item style={{ whiteSpace: 'nowrap' }} spacing={5}>
              <Typography variant="body2" component="span">
                <strong>Group #:</strong> {this.props.selectedQuote?.groupNumber}
              </Typography>
            </Grid>
            <Grid item spacing={2}>
              <Tooltip title="Show Referral Contact">
                <RecordVoiceOver
                  fontSize="small"
                  color="primary"
                  sx={{ cursor: 'pointer', verticalAlign: 'middle' }}
                  onClick={this.props.openReferralContactModal}
                />
              </Tooltip>
              {this.props.openBundleClaimsModal && (
                <Tooltip
                  title={
                    hasUnbundledClaims
                      ? 'Bundle Claims - There are claims that are not in any bundle'
                      : 'Bundle Claims'
                  }
                >
                  <AddShoppingCart
                    fontSize="small"
                    sx={{
                      cursor: 'pointer',
                      verticalAlign: 'middle',
                      marginLeft: 1,
                      color: hasUnbundledClaims ? 'red' : 'text.primary',
                    }}
                    onClick={this.props.openBundleClaimsModal}
                  />
                </Tooltip>
              )}
            </Grid>
            {this.props.selectedQuote?.isNoPtToBeScheduled && (
              <Grid item xs={12}>
                <Typography variant="caption" color="primary">
                  <strong>No PT to be scheduled for this patient</strong>
                </Typography>
              </Grid>
            )}
          </Grid>
          <Grid item xs={12} sm={12} md={5}>
            <FullWidthField
              label="Dx Codes"
              id="dxCodes"
              name="dxCodes"
              variant="outlined"
              size="small"
              disabled={this.props.viewMode}
            />
          </Grid>
          {this.props.assistantWarning ? (
            <Typography color="error">
              *Assistant Surgeon multiplier will be applied when quote is saved.
            </Typography>
          ) : (
            <></>
          )}
        </CardWithTitle>
      </>
    )
  }
}

export default QuoteDetails
